import { useApiQuery } from "@gladis/api-client";
import {
  AccountForm,
  AccountList,
  ArchivedAccounts,
  RecordBalanceForm,
  SavingsChart,
  SavingsStats,
} from "@gladis/domain-savings/ui";
import type { CurrentUserDto, MemberDto } from "@gladis/shared";
import { Button } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";
import { SectionCard } from "../../../components/section-card.js";

export const Route = createFileRoute("/_authenticated/_shell/savings")({
  component: Savings,
});

/**
 * Savings.
 *
 * Composition only — the list and the form come from the savings package, which owns what a savings
 * account is. This route decides where they appear and supplies the member list the owner selector
 * needs, which is identity's to answer and not savings'.
 *
 * The fixture chart 004 rendered here has been removed. It said in so many words that 006 would
 * replace it, and the two defects it revealed — layers keyed by display name, and a window floored
 * at the earliest data point — are fixed in `@gladis/ui` rather than worked around. The real chart
 * arrives with the read model in US4.
 */
function Savings(): ReactNode {
  const t = useT();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState<{ id: string; name: string } | null>(null);

  // Served from the cache `_authenticated` already populated — no extra request.
  const me = useApiQuery<CurrentUserDto>(["me"], "/me");
  const householdId = me.data?.activeHousehold?.id;

  /*
   * The member list, fetched only once there is a household to ask about.
   *
   * It is identity's answer, not something this module derives — FR-005 is explicit that individual
   * ownership reads the authoritative list. The server checks the chosen owner against it again on
   * submit, so this is a convenience rather than the enforcement.
   */
  const members = useApiQuery<{ members: MemberDto[] }>(
    ["members", householdId ?? "none"],
    `/households/${householdId}/members`,
    { enabled: householdId !== undefined },
  );

  return (
    <Page>
      {/*
       * ADDING AN ACCOUNT IS THIS PAGE'S PRIMARY ACTION, so it goes where `Page.Title` puts every page's:
       * the end of the title row. It used to be an underlined link in the body and, when the list was
       * empty, a second one inside the empty state — the same act in two places, in neither of which the
       * other pages put theirs.
       *
       * `aria-haspopup="dialog"` AND NOT `aria-expanded`, and it changed when the form did. `AccountForm`
       * is a modal now, with three ways to close that this control is not one of — while it is open this
       * button is behind it and inert, so it opens rather than toggles, and that is what a screen reader
       * is told. It is also no longer disabled while a balance is being recorded: the two forms shared a
       * slot in the body, and a dialog is out of the flow entirely.
       */}
      <Page.Title
        actions={
          <Button
            variant="primary"
            aria-haspopup="dialog"
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("savings.form.heading")}
          </Button>
        }
      >
        {t("nav.savings")}
      </Page.Title>
      <Page.Body>
        {/*
          THE FIGURES, FIRST — the total, what it moved this month, and who holds it.

          They were `AccountList`'s header, which put the household's headline number inside the component that
          lists accounts: below whichever form was open, and absent entirely for a household with none.
        */}
        <div className="col-span-full">
          <SavingsStats members={members.data?.members ?? []} />
        </div>

        {/*
          ALWAYS MOUNTED; `open` decides whether it is seen. See the note on `CardForm` in `cards.tsx`
          for why a native `<dialog>` cannot be conditionally rendered — and note it is outside
          `Page.Body`'s grid children in effect, since a closed dialog is `display: none` and takes no row.
        */}
        <AccountForm
          open={adding}
          onClose={() => setAdding(false)}
          members={members.data?.members ?? []}
          onCreated={() => setAdding(false)}
        />

        {/*
          RECORDING A BALANCE IS A MODAL TOO, since 2026-08-21, and it is mounted on the same terms as
          `AccountForm` above — see that note for why a native `<dialog>` cannot be rendered conditionally.

          It was an inline panel in this slot, and it was the worst placement of the pattern the two other
          forms had already left behind: it is reached from a row in the accounts table at the BOTTOM of
          the page and it appeared at the TOP of it, above the chart. The click scrolled its own form out
          of view and moved every figure the member was looking at down by the panel's height. The wrapper
          `div` is gone with it — a closed dialog is `display: none`, so it takes no grid row and the
          body's `gap-y-10` has nothing to leave a gap around.
        */}
        <RecordBalanceForm
          open={updating !== null}
          onClose={() => setUpdating(null)}
          accountId={updating?.id ?? ""}
          accountName={updating?.name}
          onRecorded={() => setUpdating(null)}
        />

        {/*
          THE CHART IN A PANEL, and it was a bare `div` until 2026-08-14 — the same defect the dashboard's
          two charts had. It is worse here than it looks: `SavingsChart` renders a range selector and an
          `<svg>` and NO HEADING, so it was an unnamed region in a screen reader's outline sitting between
          two named ones, and to a sighted reader an unframed strip between the stat cards and the accounts.
        */}
        {/*
          NO `mt-*` HERE, and it had one until 2026-08-14. `Page.Body` is a grid with `gap-y-10`, so a
          margin on a grid item ADDS to the row gap rather than replacing it — this page's last two
          sections sat 72px apart where every other page's sit 40px, which is the kind of difference that
          is invisible on one page and obvious the moment you navigate between two.
        */}
        <div className="col-span-full">
          <SectionCard
            title={t("page.savings.chartHeading")}
            hint={t("page.savings.chartHint")}
            testId="savings-chart-card"
          >
            <SavingsChart />
          </SectionCard>
        </div>

        {/*
          THE ACCOUNTS IN A PANEL, and with NO TITLE of its own on purpose. `AccountList` already renders
          `<section aria-labelledby="savings-accounts-heading">` around its own `h2`, so a title here would
          be a second heading naming one region — and the region is already named, which is what the
          accessibility rule actually asks for. The card supplies the surface and nothing else.
        */}
        <div className="col-span-full">
          <SectionCard testId="savings-accounts-card">
            <AccountList
              members={members.data?.members ?? []}
              onSelect={setUpdating}
              onOpen={(accountId) =>
                void navigate({ to: "/savings/$accountId", params: { accountId } })
              }
            />
            <ArchivedAccounts
              members={members.data?.members ?? []}
              onOpen={(accountId) =>
                void navigate({ to: "/savings/$accountId", params: { accountId } })
              }
            />
          </SectionCard>
        </div>
      </Page.Body>
    </Page>
  );
}
